// Read-only client for /api/parts_catalog/dampers. The picker in
// DamperEditSheet renders the catalog as a `<datalist>` of suggested
// submittable codes — NOT a precise build picker. Multiple catalog
// build_ids collapse to the same submittable code because dash-suffix
// segments (blowoff pair, piston) are stripped to satisfy
// DamperCornerEdit.damper_code's regex
// (^[A-Za-z][A-Za-z0-9]*(\s+\d+[Kk])?$). Precise build selection
// belongs to Phase B's catalog editor.
//
// The backend (`/api/parts_catalog/dampers`) ships a `submittable_code`
// field on each entry to avoid the frontend duplicating the conversion;
// we still keep `buildIdToDamperCode` here as a typed fallback for
// older backends and for unit tests.

export type Corner = "lf" | "rf" | "lr" | "rr";

export type CatalogDamper = {
  build_id: string;
  // Backend-provided XLSM-style code that satisfies the
  // DamperCornerEdit regex. Optional for forward-compat with older
  // backends that pre-date the submittable_code injection; new
  // backends always populate it.
  submittable_code?: string;
  family_code?: number | null;
  manufacturer_prefix?: string | null;
  inertance_kg?: number | null;
  blowoff_comp_hz?: number | null;
  blowoff_rebound_hz?: number | null;
  track_type?: string | null;
};

export type DampersByCorner = {
  lf: CatalogDamper[];
  rf: CatalogDamper[];
  lr: CatalogDamper[];
  rr: CatalogDamper[];
  all: CatalogDamper[];
};

export async function fetchPartsCatalogDampers(): Promise<DampersByCorner> {
  const r = await fetch("/api/parts_catalog/dampers");
  if (!r.ok) {
    throw new Error(`GET /api/parts_catalog/dampers failed: ${r.status}`);
  }
  const j = (await r.json()) as Partial<DampersByCorner>;
  // The endpoint always returns the {lf, rf, lr, rr, all} block;
  // missing keys would indicate a backend regression — fail loud.
  for (const k of ["lf", "rf", "lr", "rr", "all"] as const) {
    if (!Array.isArray(j[k])) {
      throw new Error(
        `/api/parts_catalog/dampers missing array for ${k}; got ${typeof j[k]}`,
      );
    }
  }
  return j as DampersByCorner;
}

// Fallback mirror of damperlib.parts_catalog.build_id_to_damper_code,
// used when the backend response omits `submittable_code` (older
// servers). Drops dash-suffix segments so the result passes the
// DamperCornerEdit regex.
export function buildIdToDamperCode(buildId: string): string {
  const raw = buildId.trim();
  if (!raw) throw new Error("build_id is empty");
  const parts = raw.split("-");
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[1]}`;
}

// Resolve a damper entry's submittable code. Prefer the
// backend-provided field; fall back to the local computation only when
// the backend didn't include it.
export function damperEntryCode(d: CatalogDamper): string | null {
  if (typeof d.submittable_code === "string" && d.submittable_code.length > 0) {
    return d.submittable_code;
  }
  if (typeof d.build_id === "string" && d.build_id.length > 0) {
    try {
      return buildIdToDamperCode(d.build_id);
    } catch {
      return null;
    }
  }
  return null;
}

// True when the typed code matches one of the catalog options for the
// given corner. The DamperEditSheet uses this for the off-catalog hint.
export function isCatalogCode(
  code: string,
  options: readonly string[],
): boolean {
  if (!code) return true;
  return options.includes(code.trim());
}

// Builds the per-corner suggested-code list. Falls back to the `all`
// pool when a corner's list is empty (matches parts_catalog.py's
// `dampers(corner)` fallback). Multiple build_ids may map to the same
// code; the Set dedupes them since the picker offers suggestions, not
// precise builds.
export function damperCodesForCorner(
  block: DampersByCorner,
  corner: Corner,
): string[] {
  const per = block[corner];
  const source = per && per.length > 0 ? per : block.all;
  const codes = new Set<string>();
  for (const d of source) {
    const code = damperEntryCode(d);
    if (code) codes.add(code);
  }
  return Array.from(codes).sort();
}
